import { useTranslation } from 'react-i18next';
import { Check, Type } from 'lucide-react';
import { PrefSection, type PrefVariant } from '@/components/PrefSection';
import { useAppFont, type AppFont } from '@/hooks/useAppFont';
import { cn } from '@/lib/utils';

/**
 * Profile-page control for the app-wide font. Mirrors `MealThumbnailSizeSwitcher`
 * / `ThemeSwitcher` — applies immediately, persists to this browser. Each tile
 * renders its label in the actual typeface it selects so the choice previews
 * itself.
 */
const OPTIONS: Array<{ value: AppFont; label: string; preview: string }> = [
  // Preview stacks mirror the --app-font definitions in index.css.
  { value: 'geomanist', label: 'Geomanist', preview: "'Geomanist', sans-serif" },
  { value: 'inter', label: 'Inter', preview: "'Inter', sans-serif" },
  { value: 'system', label: 'System', preview: 'ui-sans-serif, system-ui, sans-serif' },
];

export const FontSwitcher = ({ variant = 'card' }: { variant?: PrefVariant }) => {
  const { t } = useTranslation();
  const [font, setFont] = useAppFont();

  return (
    <PrefSection
      variant={variant}
      icon={Type}
      title={t('Font')}
      description={t('Typeface used across the app. Saved to this browser.')}
    >
      <div className="grid gap-2 sm:grid-cols-3">
          {OPTIONS.map((o) => {
            const active = font === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => setFont(o.value)}
                aria-pressed={active}
                className={cn(
                  'flex flex-col items-center gap-2 rounded-md border p-3 transition-colors',
                  active ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:bg-muted',
                )}
              >
                <span
                  aria-hidden
                  className="flex h-14 items-center text-3xl leading-none"
                  style={{ fontFamily: o.preview }}
                >
                  Ag
                </span>
                <span
                  className="flex items-center gap-1.5 text-sm font-medium"
                  style={{ fontFamily: o.preview }}
                >
                  {o.label}
                  {active && <Check className="h-3.5 w-3.5 text-primary" />}
                </span>
              </button>
            );
          })}
      </div>
    </PrefSection>
  );
};
